import { axiosInstance } from "../../utils/axios";

const BACCHUS_API_URL = "/bacchus-api/api";
const apiInst = axiosInstance;

// Collection types that can be used in the API endpoint
export type CollectionType =
  | "product"
  | "product_attribute"
  | "vendor"
  | "product_category"
  | "country"
  | "state"
  | "industry"
  | "naics"
  | "sic"
  | "intent";

export interface CollectionPermissions {
  can_write: boolean;
}

export interface Collection {
  /** Raw type from API (may include rgif_ prefix) */
  type: string;
  id: string;
  name: string;
  description: string;
  visibility: "private" | "public";
  inserted_at: string;
  updated_at: string;
  blob: {
    ids: string[] | number[];
    countryId?: string; // If type is state
  };
  copy_sent_by_user_id: string | null;
  owner_id: string;
  owner_email: string | null;
  permissions: CollectionPermissions;
}

export interface CollectionShare {
  user_id: string;
  email: string | null;
  name: string | null;
}

export type CollectionFilterBy = "owned" | "shared" | "all";

/**
 * Sorting types for collections API
 */
export type SortableField =
  | "id"
  | "name"
  | "type"
  | "inserted_at"
  | "updated_at";
export type SortDirection = "asc" | "desc";

export interface SortOptions {
  orderBy?: SortableField;
  orderDirections?: SortDirection;
}

// API response type - matches actual API response structure
export interface PaginatedResponse {
  data: Collection[];
  meta?: {
    page_size: number;
    current_page: number;
    total_count: number;
    total_pages: number;
  };
}

// Frontend pagination type - what the UI components expect
export interface PaginationData {
  page: number;
  pageSize: number;
  total: number;
  totalPages: number;
}

// Utility function to transform API meta to frontend pagination format
const transformMetaToPagination = (
  meta: PaginatedResponse["meta"]
): PaginationData | null => {
  if (!meta) return null;

  return {
    page: meta.current_page,
    pageSize: meta.page_size,
    total: meta.total_count,
    totalPages: meta.total_pages,
  } satisfies PaginationData;
};

// API Functions

/**
 * Get collections for a specific type or all types
 */
export const getCollections = async (
  type: CollectionType | "all",
  options?: {
    page?: number;
    pageSize?: number;
    filterBy?: CollectionFilterBy;
  } & SortOptions
) => {
  try {
    const params = new URLSearchParams();

    if (options?.page !== undefined) {
      params.append("page", options.page.toString());
    }
    if (options?.pageSize !== undefined) {
      params.append("page_size", options.pageSize.toString());
    }
    if (options?.filterBy && options.filterBy !== "all") {
      params.append("filter_by", options.filterBy);
    }
    if (options?.orderBy !== undefined) {
      params.append("order_by[]", options.orderBy);
    }
    if (options?.orderDirections !== undefined) {
      params.append("order_directions[]", options.orderDirections);
    }

    let endpoint: string;
    if (type === "all") {
      endpoint = `${BACCHUS_API_URL}/resources/rgif_collections`;
    } else {
      endpoint = `${BACCHUS_API_URL}/rgif_${type}_collection`;
    }

    const url = params.toString()
      ? `${endpoint}?${params.toString()}`
      : endpoint;

    const response = await apiInst.get<PaginatedResponse>(url);
    const pagination = transformMetaToPagination(response.data.meta);

    return {
      data: response.data.data,
      pagination,
      error: null,
    };
  } catch (error) {
    return {
      data: null,
      pagination: null,
      error,
    };
  }
};

/**
 * Get a specific collection by type and ID
 */
export const getCollection = async (type: CollectionType, id: string) => {
  try {
    const response = await apiInst.get<{ data: Collection }>(
      `${BACCHUS_API_URL}/rgif_${type}_collection/${id}`
    );
    return { data: response.data.data, error: null };
  } catch (error) {
    return { data: null, error };
  }
};

/**
 * Create a new collection
 */
export const createCollection = async (
  type: CollectionType,
  payload: Partial<Collection>
) => {
  try {
    const response = await apiInst.post<{ data: Collection }>(
      `${BACCHUS_API_URL}/rgif_${type}_collection`,
      payload
    );
    return { data: response.data.data, error: null };
  } catch (error) {
    return { data: null, error };
  }
};

/**
 * Update an existing collection
 */
export const updateCollection = async (
  type: CollectionType,
  id: string,
  payload: Partial<Collection>
) => {
  try {
    const response = await apiInst.patch<{ data: Collection }>(
      `${BACCHUS_API_URL}/rgif_${type}_collection/${id}`,
      payload
    );
    return { data: response.data.data, error: null };
  } catch (error) {
    return { data: null, error };
  }
};

/**
 * Delete a collection
 */
export const deleteCollection = async (type: CollectionType, id: string) => {
  try {
    await apiInst.delete(`${BACCHUS_API_URL}/rgif_${type}_collection/${id}`);
    return { error: null };
  } catch (error) {
    return { error };
  }
};

/**
 * Search collections by type and name using the dedicated endpoint
 */
export const searchCollections = async (
  type: CollectionType | "all",
  options: {
    search: string;
    page?: number;
    pageSize?: number;
  } & SortOptions
): Promise<{
  data: Collection[] | null;
  pagination: PaginationData | null;
  error: unknown;
}> => {
  try {
    const params = new URLSearchParams();
    params.append("filters[0][field]", "name");
    params.append("filters[0][value]", options.search);
    params.append("filters[0][op]", "ilike");

    if (options.page !== undefined) {
      params.append("page", options.page.toString());
    }
    if (options.pageSize !== undefined) {
      params.append("page_size", options.pageSize.toString());
    }

    // Handle sorting parameters
    if (options.orderBy !== undefined) {
      params.append("order_by[]", options.orderBy);
    }

    if (options.orderDirections !== undefined) {
      params.append("order_directions[]", options.orderDirections);
    }

    let endpoint: string;

    if (type === "all") {
      // Use generic endpoint for all types
      endpoint = `${BACCHUS_API_URL}/resources/rgif_collections?${params.toString()}`;
    } else {
      // Use dedicated endpoint for specific type
      endpoint = `${BACCHUS_API_URL}/rgif_${type}_collection?${params.toString()}`;
    }

    const response = await apiInst.get<PaginatedResponse>(endpoint);

    // Transform API response to match frontend expectations
    const pagination = transformMetaToPagination(response.data.meta);

    return {
      data: response.data.data,
      pagination,
      error: null,
    };
  } catch (error) {
    return {
      data: null,
      pagination: null,
      error,
    };
  }
};

/**
 * Get the current share list for a collection
 */
export const getCollectionShares = async (type: CollectionType, id: string) => {
  try {
    const response = await apiInst.get<{ data: CollectionShare[] }>(
      `${BACCHUS_API_URL}/rgif_${type}_collection/${id}/shares`
    );
    return { data: response.data.data, error: null };
  } catch (error) {
    return { data: null, error };
  }
};

/**
 * Replace the share list for a collection (owner only).
 * Passing an empty array revokes all shares.
 */
export const updateCollectionShares = async (
  type: CollectionType,
  id: string,
  emails: string[]
) => {
  try {
    const response = await apiInst.put<{ success: string[]; failed: string[] }>(
      `${BACCHUS_API_URL}/rgif_${type}_collection/${id}/shares`,
      { shares: emails.map((email) => ({ email })) }
    );
    return { data: response.data, error: null };
  } catch (error) {
    return { data: null, error };
  }
};

/**
 * Check if a collection name is already in use
 * Returns true if name is taken, false if available
 */
export const checkCollectionNameAvailability = async (
  /** The name to check for availability */
  nameToCheck: string,
  /** Optional collection ID to exclude from the check (useful for edit mode) */
  excludeId?: string
) => {
  try {
    const params = new URLSearchParams();
    params.append("filters[0][field]", "name");
    params.append("filters[0][value]", nameToCheck);

    const response = await apiInst.get<{ data: Collection[] }>(
      `${BACCHUS_API_URL}/resources/rgif_collections`,
      { params }
    );

    let collections = response.data.data;

    if (excludeId) {
      collections = collections.filter(
        (collection) => collection.id !== excludeId
      );
    }

    return { isTaken: collections.length > 0, error: null };
  } catch (error) {
    return { isTaken: false, error };
  }
};
